"use client";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  CANCELLATION_REFUND,
  CONTACT_US,
  DATA_DELETION_REQUEST,
  HOME_PAGE,
  PLATFORM_GOOGLE,
  PLATFORM_META,
  PLATFORM_SEO,
  PRIVACY_POLICY,
  SHIPPING_DELIVERY,
  SOLUTION_AD_CREATION,
  SOLUTION_CREATIVE_INTELLIGENCE,
  SOLUTION_TESTING_OPTIMIZATION,
  SOLUTION_WORKSPACE_ASSETS,
  TERMS,
} from "routes";

const footerColumns = [
  {
    title: "Platform",
    links: [
      { label: "Meta", href: PLATFORM_META },
      { label: "Google", href: PLATFORM_GOOGLE },
      { label: "SEO", href: PLATFORM_SEO },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Ad Creation", href: SOLUTION_AD_CREATION },
      { label: "Creative Intelligence", href: SOLUTION_CREATIVE_INTELLIGENCE },
      { label: "Testing & Optimization", href: SOLUTION_TESTING_OPTIMIZATION },
      { label: "Workspace & Assets", href: SOLUTION_WORKSPACE_ASSETS },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Contact Us", href: CONTACT_US },
      { label: "Privacy Policy", href: PRIVACY_POLICY },
      { label: "Terms & Conditions", href: TERMS },
      { label: "Data Deletion Request", href: DATA_DELETION_REQUEST },
      { label: "Cancellation & Refund", href: CANCELLATION_REFUND },
      { label: "Shipping & Delivery", href: SHIPPING_DELIVERY },
    ],
  },
  {
    title: "Contact us",
    links: [
      {
        label:
          "Unitech World (Unitech Cyber Park), Sector 39, Gurugram,Haryana 122003",
        href: "https://www.google.com/maps/search/unitech+world+unitech+cyber+park+sector+39+gurugram+haryana+122003/@28.4433425,77.04557,16z?entry=ttu&g_ep=EgoyMDI2MDYyMS4wIKXMDSoASAFQAw%3D%3D",
      },
      { label: "Support@imagive.ai", href: "mailto:Support@imagive.ai" },
    ],
  },
];

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/imagiveai/",
    icon: "/assets/homePage/footer/linkedin.svg",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/imagiveai",
    icon: "/assets/homePage/footer/facebook.svg",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/imagive.ai/",
    icon: "/assets/homePage/footer/instagram.svg",
  },
];

export default function Footer() {
  const [openSections, setOpenSections] = useState(
    Array(footerColumns.length).fill(false),
  );

  const handleToggle = (index) => {
    const newOpenSections = [...openSections];
    newOpenSections[index] = !newOpenSections[index];
    setOpenSections(newOpenSections);
  };

  return (
    <footer
      className="relative bg-[#F4F4F6] overflow-hidden border-[0.5px] border-black/10 rounded-[20px] m-5"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <div className="relative z-10 w-[90%] mx-auto pt-12 pb-6">
        {/* Top section: logo + columns */}
        <div className="flex flex-col lg:flex-row lg:gap-16 mb-4">
          {/* Left: Logo + tagline + contact */}
          <div className="flex flex-col gap-10 w-full lg:w-[300px] shrink-0 lg:mb-0">
            <div>
              <Link href={HOME_PAGE} className="inline-block ">
                <Image
                  src="/assets/images/imagiveLogo.svg"
                  width={150}
                  height={50}
                  alt="Imagive Logo"
                  style={{ height: "auto", width: "140px" }}
                  priority
                />
              </Link>
              <p className="text-[14px] leading-relaxed text-[#656668] max-w-[200px]">
                Sign up now for FREE and get access to all our premium features.
              </p>
            </div>
          </div>

          {/* Right: Link columns */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-2 lg:gap-x-8">
            {footerColumns.map((col, index) => (
              <div key={col.title}>
                {/* Mobile accordion header */}
                <button
                  onClick={() => handleToggle(index)}
                  className="w-full flex justify-between items-center py-3 border-b border-[#00000018] lg:border-0 lg:pb-0 lg:cursor-default"
                >
                  <span className="text-[11px] tracking-[0.12em] font-semibold uppercase text-[#283245]">
                    {col.title}
                  </span>
                  <span className="lg:hidden text-[#283245]">
                    {openSections[index] ? (
                      <ChevronUp size={16} strokeWidth={2.5} />
                    ) : (
                      <ChevronDown size={16} strokeWidth={2.5} />
                    )}
                  </span>
                </button>

                {/* Links */}
                <ul
                  className={clsx("space-y-[10px] mt-4", {
                    block: openSections[index],
                    "hidden lg:block": !openSections[index],
                  })}
                >
                  {col.links.map((link) => (
                    <li key={link.label} className="flex items-center">
                      <Link
                        href={link.href}
                        className="text-[14px] text-[#8E8E8E] hover:text-[#283245] transition-colors duration-150"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Watermark */}
      <div
        aria-hidden="true"
        className=" relative pointer-events-none select-none w-full px-4 -mt-[10px]  lg:-mb-[4px] overflow-hidden"
      >
        <Image
          src="/assets/homePage/footer/Imagive.ai.svg"
          width={1300}
          height={150}
          alt=""
          className="w-full opacity-[0.06]  "
          style={{ height: "auto" }}
        />

        {/* Bottom bar: copyright pill + divider + social badges */}
        <div className="static lg:absolute lg:bottom-[40px] left-0 right-0 flex flex-col lg:flex-row items-center gap-4 lg:gap-4 px-4 lg:px-16 pb-6 lg:pb-0 -mt-4 lg:mt-0 pointer-events-auto">
          {/* Mobile: plain centered text */}
          <p className="lg:hidden text-[12px] text-[#282828] text-center">
            Copyright © {new Date().getFullYear()} Imagive.ai All rights
            reserved.
          </p>
          {/* Desktop: full-width divider line behind everything */}
          <hr className="hidden lg:block absolute left-16 right-16 top-1/2 -translate-y-1/2 border-0 h-[1px] bg-[#D7D7D7] z-0" />
          {/* Desktop: pill */}
          <p className="hidden lg:block relative z-10 shrink-0 whitespace-nowrap text-[12px] bg-[#FBFAF7] rounded-full text-[#282828] px-3 py-1">
            Copyright © {new Date().getFullYear()} Imagive. All rights reserved.
          </p>

          {/* Social badges */}
          <div className="relative z-10 flex items-center gap-4 lg:ml-auto">
            {socialLinks.map((social) => (
              <Link
                key={social.label}
                href={social.href}
                aria-label={social.label}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full border-[0.5px] border-black/10 bg-[#F4F4F6] transition-transform duration-150 hover:-translate-y-0.5"
              >
                <Image
                  src={social.icon}
                  width={19}
                  height={19}
                  alt={social.label}
                  style={{ width: "19.094px", height: "19.11px" }}
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
